import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { CacheName, clearCache } from '@/apis/dev';
import { precacheSearchTree } from '@/apis/searchTree';
import { ExtendedSearchTreeNode } from '@/types/searchTreeTypes';
import { logger } from '@/util';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';

/**
 * Will cause trigger a call the backend api to precache the results of the
 * filter used to determine what contacts a user can see. Although this is
 * technically a mutation, we are making use of useQuery instead of useMutation
 * to simplify avoiding re-runnings the same thing concurrently. react-query
 * will take care of that for us by caching whether or not the precache is
 * already being performed.
 *
 * [LIST-721] Every advanced search request sent to the backend is always
 * intersected with an 'all' filter using the user's permissions. This normally
 * involves a time consuming query, so precaching it while the user is still
 * working on their search terms can be beneficial.
 */
export const usePrecacheUserContactPerms = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const sub = user?.sub ?? '';
  const printTime = useFeatureToggle('Print Advanced Search Time');
  const useAdvancedSearchII = useFeatureToggle('Use Advanced Search II');

  return useQuery({
    queryKey: ['precacheusercontactperms', sub, domainCode],
    queryFn: async () => {
      if (sub) {
        const token = await getAccessTokenSilently();
        const tree: ExtendedSearchTreeNode = {
          op: 'filter',
          filter: {
            name: 'all',
          },
          filterContext: {
            userPerm: { domainCode, sub },
          },
          printTime,
          useAdvancedSearchII,
        };
        logger.info('Starting precache of user contacts');
        const t0 = performance.now();
        const data = await precacheSearchTree(token, tree, domainCode);
        const timeMs = performance.now() - t0;
        logger.info(`Finished precache of user contacts [${Math.round(timeMs)}ms]`);
        return data;
      }
      return {} as ReturnType<typeof precacheSearchTree>;
    },

    // Currently backend cache is 10 minutes, but calling the precache endpoint
    // won't caure data to be fetched again so this keeps things up to date
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useClearCache = (cacheName: CacheName) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['clearcache', cacheName],
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      // console.debug(`Starting clear cache ${cacheName}`);
      await clearCache(token, cacheName);
      // console.debug(`Finished clearing cache ${cacheName}`);

      // We also want to invalidate relevant queries
      switch (cacheName) {
        case 'redis.ListContact':
          queryClient.removeQueries({ queryKey: ['list'] });
          break;
        case 'redis.AdvancedSearch':
          queryClient.removeQueries({ queryKey: ['searches'] });
          break;
        case 'redis.All':
          queryClient.removeQueries({ queryKey: ['list'] });
          queryClient.removeQueries({ queryKey: ['searches'] });
          break;
        case 'memory.all':
          // N/A
          break;
      }
    },
  });
};
